import type { CourseObstacle } from './drivingCourse';

/** 한 물리 서브스텝에서 충돌 판정에 필요한 차량 운동만 추린 값입니다. */
export interface VehicleCollisionMotion {
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
  fromHeading: number;
  toHeading: number;
  speed: number;
  lateralSpeed: number;
  yawRate: number;
}

/** 앞뒤 차축을 잇는 캡슐로 차체 바닥 면적을 근사합니다. */
export interface VehicleCollisionFootprint {
  frontX: number;
  rearX: number;
  halfWidth: number;
}

export interface CourseCollisionResult {
  x: number;
  z: number;
  speed: number;
  lateralSpeed: number;
  yawRate: number;
  /** 충돌 전 법선 접근 속도를 0~1로 정규화한 값입니다. */
  impact: number;
  /** 장애물에서 차량을 향하는 월드 평면 법선입니다. */
  normalX: number;
  normalZ: number;
  obstacleId: string | null;
}

interface CollisionCandidate {
  obstacle: CourseObstacle;
  /** 충돌한 캡슐 축 샘플의 차량 로컬 X입니다. 회전 접촉 속도에 사용합니다. */
  localX: number;
  time: number;
  normalX: number;
  normalZ: number;
  penetration: number;
}

const CONTACT_EPSILON = 0.015;
const ROOT_EPSILON = 1e-9;
const IMPACT_FULL_SPEED = 14;
const IMPACT_DEAD_ZONE = 0.4;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function sampleCenter(
  centerX: number,
  centerZ: number,
  heading: number,
  localX: number
): { x: number; z: number } {
  return {
    x: centerX + localX * Math.cos(heading),
    z: centerZ - localX * Math.sin(heading),
  };
}

function collisionSampleOffsets(
  footprint: VehicleCollisionFootprint,
  halfWidth: number
): readonly number[] {
  const rear = Math.min(footprint.rearX, footprint.frontX);
  const front = Math.max(footprint.rearX, footprint.frontX);
  const length = front - rear;
  if (length <= ROOT_EPSILON) {
    return [rear];
  }
  // 원 3개 고정보다 촘촘히 샘플링해 캡슐 측면의 scallop 오차를 약 3cm 이하로 둡니다.
  const segmentCount = Math.max(1, Math.ceil(length / Math.max(0.2, halfWidth * 0.5)));
  return Array.from(
    { length: segmentCount + 1 },
    (_, index) => rear + length * (index / segmentCount)
  );
}

/** 움직이는 점과 정적 원의 첫 접촉 시간(0~1)을 구합니다. */
function firstCircleContact(
  startX: number,
  startZ: number,
  endX: number,
  endZ: number,
  obstacle: CourseObstacle,
  radius: number
): Omit<CollisionCandidate, 'obstacle' | 'localX'> | null {
  const relativeX = startX - obstacle.x;
  const relativeZ = startZ - obstacle.z;
  const deltaX = endX - startX;
  const deltaZ = endZ - startZ;
  const radiusSquared = radius * radius;
  const startDistanceSquared = relativeX * relativeX + relativeZ * relativeZ;
  const movementSquared = deltaX * deltaX + deltaZ * deltaZ;

  let time: number;
  if (startDistanceSquared <= radiusSquared) {
    const startDistance = Math.sqrt(startDistanceSquared);
    let normalX: number;
    let normalZ: number;
    if (startDistance > ROOT_EPSILON) {
      normalX = relativeX / startDistance;
      normalZ = relativeZ / startDistance;
    } else if (movementSquared > ROOT_EPSILON) {
      const movement = Math.sqrt(movementSquared);
      normalX = -deltaX / movement;
      normalZ = -deltaZ / movement;
    } else {
      normalX = 1;
      normalZ = 0;
    }

    // 이미 겹쳐 있지만 바깥으로 빠지는 중이면 진행을 막지 않습니다.
    if (deltaX * normalX + deltaZ * normalZ >= 0) {
      return null;
    }
    return {
      time: 0,
      normalX,
      normalZ,
      penetration: Math.max(0, radius - startDistance),
    };
  }

  if (movementSquared <= ROOT_EPSILON) {
    return null;
  }

  const b = 2 * (relativeX * deltaX + relativeZ * deltaZ);
  const c = startDistanceSquared - radiusSquared;
  const discriminant = b * b - 4 * movementSquared * c;
  if (discriminant < 0) {
    return null;
  }

  time = (-b - Math.sqrt(discriminant)) / (2 * movementSquared);
  if (time < 0 || time > 1) {
    return null;
  }

  const hitX = startX + deltaX * time;
  const hitZ = startZ + deltaZ * time;
  const normalLength = Math.hypot(hitX - obstacle.x, hitZ - obstacle.z);
  if (normalLength <= ROOT_EPSILON) {
    return null;
  }
  return {
    time,
    normalX: (hitX - obstacle.x) / normalLength,
    normalZ: (hitZ - obstacle.z) / normalLength,
    penetration: 0,
  };
}

/**
 * 코스 장애물과 차량 캡슐의 가장 이른 접촉을 연속 판정하고 속도를 반사·감쇠합니다.
 * Three.js 객체를 사용하지 않는 순수 함수라 1/120초 물리 서브스텝의 결정성을 유지합니다.
 */
export function resolveCourseObstacleCollision(
  motion: VehicleCollisionMotion,
  footprint: VehicleCollisionFootprint,
  obstacles: readonly CourseObstacle[] | undefined
): CourseCollisionResult {
  const unchanged: CourseCollisionResult = {
    x: motion.toX,
    z: motion.toZ,
    speed: motion.speed,
    lateralSpeed: motion.lateralSpeed,
    yawRate: motion.yawRate,
    impact: 0,
    normalX: 0,
    normalZ: 0,
    obstacleId: null,
  };
  if (obstacles === undefined || obstacles.length === 0) {
    return unchanged;
  }

  const halfWidth = Math.max(0.35, footprint.halfWidth);
  const offsets = collisionSampleOffsets(footprint, halfWidth);
  let earliest: CollisionCandidate | null = null;

  for (const obstacle of obstacles) {
    const combinedRadius = halfWidth + obstacle.collider.radius;
    for (const localX of offsets) {
      const start = sampleCenter(
        motion.fromX,
        motion.fromZ,
        motion.fromHeading,
        localX
      );
      const end = sampleCenter(
        motion.toX,
        motion.toZ,
        motion.toHeading,
        localX
      );
      const candidate = firstCircleContact(
        start.x,
        start.z,
        end.x,
        end.z,
        obstacle,
        combinedRadius
      );
      if (candidate === null) {
        continue;
      }
      if (earliest === null || candidate.time < earliest.time - ROOT_EPSILON) {
        earliest = { obstacle, localX, ...candidate };
      }
    }
  }

  if (earliest === null) {
    return unchanged;
  }

  const hitTime = Math.max(0, earliest.time - 1e-5);
  const x = motion.fromX
    + (motion.toX - motion.fromX) * hitTime
    + earliest.normalX * (earliest.penetration + CONTACT_EPSILON);
  const z = motion.fromZ
    + (motion.toZ - motion.fromZ) * hitTime
    + earliest.normalZ * (earliest.penetration + CONTACT_EPSILON);

  const forwardX = Math.cos(motion.toHeading);
  const forwardZ = -Math.sin(motion.toHeading);
  const rightX = Math.sin(motion.toHeading);
  const rightZ = Math.cos(motion.toHeading);
  /* 접촉한 앞/뒤 지점에는 중심 선속도뿐 아니라 yaw의 접선 속도도 더해집니다. */
  const angularVelocityX = -earliest.localX * Math.sin(motion.toHeading) * motion.yawRate;
  const angularVelocityZ = -earliest.localX * Math.cos(motion.toHeading) * motion.yawRate;
  const worldVelocityX =
    forwardX * motion.speed + rightX * motion.lateralSpeed + angularVelocityX;
  const worldVelocityZ =
    forwardZ * motion.speed + rightZ * motion.lateralSpeed + angularVelocityZ;
  const normalSpeed =
    worldVelocityX * earliest.normalX + worldVelocityZ * earliest.normalZ;
  const closingSpeed = Math.max(0, -normalSpeed);
  const impact = clamp01((closingSpeed - IMPACT_DEAD_ZONE) / IMPACT_FULL_SPEED);

  let resolvedVelocityX = worldVelocityX - angularVelocityX;
  let resolvedVelocityZ = worldVelocityZ - angularVelocityZ;
  if (normalSpeed < 0) {
    const tangentVelocityX = worldVelocityX - normalSpeed * earliest.normalX;
    const tangentVelocityZ = worldVelocityZ - normalSpeed * earliest.normalZ;
    const outgoingNormalSpeed = -normalSpeed * earliest.obstacle.collider.restitution;
    // 스침 충돌의 접선 속도를 통째로 잃지 않도록 마찰 손실을 법선 충격에 비례시킵니다.
    const tangentRetention =
      1 - (1 - earliest.obstacle.collider.tangentRetention) * impact;
    resolvedVelocityX =
      tangentVelocityX * tangentRetention
      + earliest.normalX * outgoingNormalSpeed
      - angularVelocityX;
    resolvedVelocityZ =
      tangentVelocityZ * tangentRetention
      + earliest.normalZ * outgoingNormalSpeed
      - angularVelocityZ;
  }

  return {
    x,
    z,
    speed: resolvedVelocityX * forwardX + resolvedVelocityZ * forwardZ,
    lateralSpeed: resolvedVelocityX * rightX + resolvedVelocityZ * rightZ,
    yawRate: motion.yawRate * Math.max(0.28, 1 - impact * 0.72),
    impact,
    normalX: earliest.normalX,
    normalZ: earliest.normalZ,
    obstacleId: earliest.obstacle.id,
  };
}

/**
 * 마지막 접촉이 충분히 분리됐는지 검사합니다. 작은 depenetration epsilon만으로 contact가
 * 매 서브스텝 enter/exit를 반복하지 않도록 실제 반경보다 releaseMargin만큼 넓게 봅니다.
 */
export function isCourseObstacleContactActive(
  x: number,
  z: number,
  heading: number,
  footprint: VehicleCollisionFootprint,
  obstacles: readonly CourseObstacle[] | undefined,
  obstacleId: string | null,
  releaseMargin = 0.12
): boolean {
  if (obstacles === undefined || obstacleId === null) {
    return false;
  }
  const obstacle = obstacles.find((candidate) => candidate.id === obstacleId);
  if (obstacle === undefined) {
    return false;
  }
  const halfWidth = Math.max(0.35, footprint.halfWidth);
  const contactRadius = halfWidth + obstacle.collider.radius + Math.max(0, releaseMargin);
  const contactRadiusSquared = contactRadius * contactRadius;
  return collisionSampleOffsets(footprint, halfWidth).some((localX) => {
    const sample = sampleCenter(x, z, heading, localX);
    return (sample.x - obstacle.x) ** 2 + (sample.z - obstacle.z) ** 2
      <= contactRadiusSquared;
  });
}
